import { UserRepository } from '@/repository/user/user.repository';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { distinctUntilChanged, from, lastValueFrom, map, merge, Observable, skip } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth.service';

export class OktaService extends AuthService {
  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly oktaAuthStateService = inject(OktaAuthStateService);
  private readonly router = inject(Router);
  private readonly userRepository = inject(UserRepository);

  // Custom okta auth instance configured to use
  // tinea's issuer instead of the default one
  // Only needed for login/logout
  private readonly tineaAuth = new OktaAuth({
    issuer: environment.tinea_issuer,
    clientId: environment.okta_client_id,
    redirectUri: window.location.origin + environment.okta_redirectPath,
    scopes: environment.okta_scope,
  });

  private readonly tineaDomains = ['equans.com', 'external.equans.com', 'engie.com', 'external.engie.com'];

  private isTineaEmail(email: string = ''): boolean {
    return this.tineaDomains.includes(email.substring(email.indexOf('@') + 1));
  }

  getAuthToken(): string {
    const token = this.oktaAuth.getAccessToken() ?? '';
    this.authToken$.next(token);
    return token;
  }

  isAuthenticated(): Observable<boolean> {
    return from(this.oktaAuth.isAuthenticated());
  }

  isAuthenticated$(): Observable<boolean> {
    const authStateChanges$ = this.oktaAuthStateService.authState$.pipe(
      skip(1),
      map((authState) => !!authState.isAuthenticated),
    );
    return merge(this.isAuthenticated(), authStateChanges$).pipe(distinctUntilChanged());
  }

  login(email: string): void {
    if (this.isTineaEmail(email)) {
      this.tineaAuth.signInWithRedirect({ loginHint: email });
    } else {
      this.oktaAuth.signInWithRedirect({ loginHint: email });
    }
  }

  logout(): void {
    lastValueFrom(this.userRepository.getProfile())
      .then((profile) => {
        if (this.isTineaEmail(profile.email)) {
          this.tineaAuth.signOut();
        } else {
          this.oktaAuth.signOut();
        }
      })
      .catch(() => {
        // When unable to get user
        // just try to logout with default okta config
        this.oktaAuth.signOut();
      });
  }

  storeTokensFromRedirect(): Observable<void> {
    return from(this.oktaAuth.storeTokensFromRedirect());
  }

  handleRedirect(): Observable<void> {
    const originalUri = this.oktaAuth.getOriginalUri() ?? '/'; // default to root path

    return from(this.router.navigateByUrl(originalUri).then(() => {}));
  }
}
